import { lazy, Suspense, useEffect, useRef, useState } from "react";
import { Switch, Route, Router as WouterRouter, Redirect, useLocation } from "wouter";
import { ClerkProvider, useAuth as useClerkAuth, useUser as useClerkUser, useClerk } from "@clerk/react";
import * as Sentry from "@sentry/react";
import { clerkBaseAppearance } from "@/lib/clerkAppearance";
import { QueryClientProvider } from "@tanstack/react-query";
import { Toaster } from "@/components/ui/toaster";
import { TooltipProvider } from "@/components/ui/tooltip";
import { useAuth, AuthProvider } from "@workspace/replit-auth-web";
import { queryClient } from "@/lib/api";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import NotFound from "@/pages/not-found";
import { getListDepartmentsQueryOptions, setAuthTokenProvider } from "@workspace/api-client-react";

const LandingPage = lazy(() => import("@/pages/LandingPage"));
const FeaturesPage = lazy(() => import("@/pages/FeaturesPage"));
const AboutPage = lazy(() => import("@/pages/AboutPage"));
const PrivacyPage = lazy(() => import("@/pages/PrivacyPage"));
const TermsPage = lazy(() => import("@/pages/TermsPage"));
const ComingSoonPage = lazy(() => import("@/pages/ComingSoonPage"));
const LoginPage = lazy(() => import("@/pages/LoginPage"));
const SignUpPage = lazy(() => import("@/pages/SignUpPage"));
const SurveyPage = lazy(() => import("@/pages/SurveyPage"));
const UnlockReport = lazy(() => import("@/pages/UnlockReport"));
const Onboarding = lazy(() => import("@/pages/Onboarding"));
const CEODashboard = lazy(() => import("@/pages/CEODashboard"));
const DepartmentDashboard = lazy(() => import("@/pages/DepartmentDashboard"));
const TopicsList = lazy(() => import("@/pages/TopicsList"));
const TopicDetail = lazy(() => import("@/pages/TopicDetail"));
const UploadData = lazy(() => import("@/pages/UploadData"));
const History = lazy(() => import("@/pages/History"));
const Archive = lazy(() => import("@/pages/Archive"));
const Notifications = lazy(() => import("@/pages/Notifications"));
const Settings = lazy(() => import("@/pages/Settings"));
const Integrations = lazy(() => import("@/pages/Integrations"));
const Pricing = lazy(() => import("@/pages/Pricing"));
const PaymentSuccess = lazy(() => import("@/pages/PaymentSuccess"));
const NotebookList = lazy(() => import("@/pages/NotebookList"));
const TasksPage = lazy(() => import("@/pages/TasksPage"));
const ReviewsPage = lazy(() => import("@/pages/ReviewsPage"));
const ScanBatchesPage = lazy(() => import("@/pages/ScanBatchesPage"));
const AdsPage = lazy(() => import("@/pages/AdsPage"));
const AdminConsole = lazy(() => import("@/pages/AdminConsole"));
const AutomationDashboard = lazy(() => import("@/pages/AutomationDashboard"));
const OnboardClient = lazy(() => import("@/pages/OnboardClient"));
const AcceptInvite = lazy(() => import("@/pages/AcceptInvite"));
const EmailPage = lazy(() => import("@/pages/EmailPage"));
const CrmContacts = lazy(() => import("@/pages/CrmContacts"));
const CrmContactDetail = lazy(() => import("@/pages/CrmContactDetail"));
const PendingAccess = lazy(() => import("@/pages/PendingAccess"));
const SelectRolePage = lazy(() => import("@/pages/SelectRolePage"));

function ShellSkeleton() {
  return (
    <div className="flex h-screen w-full overflow-hidden bg-background">
      <aside className="hidden md:flex w-64 shrink-0 flex-col bg-[hsl(var(--sidebar))] border-r border-[hsl(var(--sidebar-border))]">
        <div className="h-16 flex items-center px-6 border-b border-[hsl(var(--sidebar-border))]">
          <div className="w-28 h-6 rounded-lg skeleton-shimmer" />
        </div>
        <div className="flex flex-col gap-1.5 px-3 py-4">
          {[...Array(4)].map((_, i) => (
            <div
              key={i}
              className="h-9 rounded-lg skeleton-shimmer"
              style={{ animationDelay: `${i * 80}ms` }}
            />
          ))}
          <div className="mt-4 mb-1 px-3">
            <div className="h-3 w-20 rounded skeleton-shimmer" />
          </div>
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className="h-9 rounded-lg skeleton-shimmer"
              style={{ animationDelay: `${(i + 4) * 80}ms` }}
            />
          ))}
        </div>
      </aside>
      <div className="flex flex-col flex-1 min-w-0">
        <div className="h-16 border-b border-border bg-background/80 flex items-center justify-end px-4 gap-2">
          <div className="w-8 h-8 rounded-full skeleton-shimmer" />
        </div>
        <div className="flex-1 p-6 space-y-4">
          <div className="h-8 w-56 rounded-xl skeleton-shimmer" />
          <div className="h-4 w-40 rounded-lg skeleton-shimmer" />
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-6">
            {[...Array(4)].map((_, i) => (
              <div
                key={i}
                className="h-28 rounded-xl skeleton-shimmer"
                style={{ animationDelay: `${i * 60}ms` }}
              />
            ))}
          </div>
          <div className="h-48 rounded-xl skeleton-shimmer mt-2" />
        </div>
      </div>
    </div>
  );
}

function LoadingScreen() {
  return (
    <div className="min-h-screen w-full flex items-center justify-center bg-background">
      <div className="flex flex-col items-center gap-4">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-sky-400 via-indigo-500 to-purple-600 animate-pulse" />
        <p className="text-sm text-slate-500">Loading Clairo...</p>
      </div>
    </div>
  );
}

function AppBootstrap() {
  const { isAuthenticated, isLoading } = useAuth();
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      queryClient.prefetchQuery(getListDepartmentsQueryOptions());
    }
  }, [isAuthenticated, isLoading]);
  return null;
}

/**
 * Bridges Clerk's session token into the generated API client. Without this
 * the backend never sees the user's identity on cross-origin/proxied API
 * requests.
 */
function ClerkAuthBridge() {
  const { getToken, isLoaded, orgId } = useClerkAuth();
  const { user: clerkUser, isLoaded: userLoaded } = useClerkUser();
  // Clerk's `getToken` identity changes on every render in some scenarios.
  // Holding it in a ref lets the registration effect fire exactly once per
  // Clerk-load transition instead of churning the auth-token provider on
  // every render (which previously triggered a useSyncExternalStore loop).
  const getTokenRef = useRef(getToken);
  useEffect(() => {
    getTokenRef.current = getToken;
  });
  useEffect(() => {
    if (!isLoaded) return;
    setAuthTokenProvider(() => getTokenRef.current());
    return () => {
      setAuthTokenProvider(null);
    };
  }, [isLoaded]);
  // Gate Sentry user updates on a stable identity signature (id + email + org)
  // rather than on the raw `clerkUser` object reference, which can change on
  // every render even when the underlying identity hasn't changed.
  const userId = clerkUser?.id ?? null;
  const userEmail = clerkUser?.primaryEmailAddress?.emailAddress ?? null;
  useEffect(() => {
    if (!userLoaded) return;
    if (userId) {
      Sentry.setUser({
        id: userId,
        email: userEmail ?? undefined,
        orgId: orgId ?? undefined,
      });
      Sentry.addBreadcrumb({
        category: "auth",
        message: "User signed in",
        level: "info",
      });
    } else {
      Sentry.setUser(null);
      Sentry.addBreadcrumb({
        category: "auth",
        message: "User signed out",
        level: "info",
      });
    }
  }, [userLoaded, userId, userEmail, orgId]);
  return null;
}

/**
 * Tracks the current wouter route on every Sentry event so triagers can see
 * which page the user was on when an error occurred. Also drops a navigation
 * breadcrumb on every change.
 */
function SentryRouteTracker() {
  const [location] = useLocation();
  useEffect(() => {
    Sentry.setTag("route", location);
    Sentry.addBreadcrumb({
      category: "navigation",
      message: `Navigated to ${location}`,
      level: "info",
    });
  }, [location]);
  return null;
}

function needsRolePicker(
  user: { roles?: string[] | null } | null,
  activeRole: string | null,
  location: string,
): boolean {
  if (!user) return false;
  if (activeRole !== null) return false;
  if ((user.roles?.length ?? 0) <= 1) return false;
  return location !== "/select-role" && location !== "/onboarding";
}

function ProtectedRoute({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading, user, activeRole } = useAuth();
  const [location] = useLocation();

  if (isLoading) return <LoadingScreen />;
  if (!isAuthenticated) return <Redirect to="/login" />;
  if (!user?.orgId && location !== "/onboarding" && location !== "/pending-access") {
    const isSuperAdmin = user?.roles?.includes("super_admin") ?? false;
    if (isSuperAdmin && location === "/admin") return <Component />;
    if (isSuperAdmin) return <Redirect to="/admin" />;
    return <Redirect to="/pending-access" />;
  }
  if (needsRolePicker(user, activeRole, location)) return <Redirect to="/select-role" />;
  return <Component />;
}

/**
 * Reads Clerk's signed-in state. In dev builds only, an e2e test may force
 * `clerkSignedIn=true` by setting `sessionStorage["__e2e_force_clerk_signed_in"]`
 * to "1". This is the only way Playwright can reproduce the corrupt
 * "Clerk session present + backend null user" scenario without standing up
 * real Clerk session infrastructure. See `e2e/tests/v3-auth-redirects.spec.ts`.
 */
function useClerkSignedInState(): { clerkLoaded: boolean; clerkSignedIn: boolean } {
  const { isLoaded, isSignedIn } = useClerkUser();
  const [forced, setForced] = useState<boolean>(() => {
    if (!import.meta.env.DEV) return false;
    try {
      return (
        typeof window !== "undefined" &&
        window.sessionStorage.getItem("__e2e_force_clerk_signed_in") === "1"
      );
    } catch {
      return false;
    }
  });
  // The seam lives in sessionStorage, which doesn't fire change events for the
  // same window. The recovery effect dispatches `__e2e_clerk_seam_changed`
  // after clearing the flag so the hook can reactively flip back to Clerk's
  // real signed-in state and the public LoginPage can render.
  useEffect(() => {
    if (!import.meta.env.DEV) return;
    const sync = () => {
      try {
        setForced(window.sessionStorage.getItem("__e2e_force_clerk_signed_in") === "1");
      } catch {
        setForced(false);
      }
    };
    window.addEventListener("__e2e_clerk_seam_changed", sync);
    return () => window.removeEventListener("__e2e_clerk_seam_changed", sync);
  }, []);
  if (forced) return { clerkLoaded: true, clerkSignedIn: true };
  return { clerkLoaded: isLoaded, clerkSignedIn: !!isSignedIn };
}

function PublicOnlyRoute({ component: Component }: { component: React.ComponentType }) {
  const { isAuthenticated, isLoading } = useAuth();
  // Also consult Clerk directly: the workspace `useAuth()` only flips
  // `isAuthenticated` after `/api/auth/user` resolves with a non-null user.
  // If Clerk already has a session, we must not mount <SignIn/> — it will
  // refuse to render and leave the user stranded on an empty card.
  const { clerkLoaded, clerkSignedIn } = useClerkSignedInState();
  const clerk = useClerk();

  // Corrupt half-session recovery: Clerk thinks we're signed in, but our
  // backend doesn't recognize the user (e.g. their server-side session
  // expired or the user was deleted). Sign them out of Clerk so they can
  // sign back in cleanly, and flag the LoginPage to explain why.
  const stuckHalfSession = clerkLoaded && !isLoading && clerkSignedIn && !isAuthenticated;
  useEffect(() => {
    if (!stuckHalfSession) return;
    try {
      sessionStorage.setItem("clairo_session_expired", "1");
    } catch {
      // sessionStorage may be unavailable (private mode); the LoginPage
      // simply won't show the notice in that case.
    }
    if (import.meta.env.DEV) {
      // Clear the e2e test seam so the recovery effect doesn't re-fire
      // forever on the next render (see useClerkSignedInState), then notify
      // the hook to re-read it — sessionStorage doesn't emit events for
      // same-window writes.
      try {
        window.sessionStorage.removeItem("__e2e_force_clerk_signed_in");
        window.dispatchEvent(new Event("__e2e_clerk_seam_changed"));
      } catch {
        /* ignore */
      }
    }
    void clerk.signOut();
  }, [stuckHalfSession, clerk]);

  if (isLoading || !clerkLoaded) return <LoadingScreen />;
  if (stuckHalfSession) return <LoadingScreen />;
  if (isAuthenticated) return <Redirect to="/dashboard" />;
  return <Component />;
}

function Router() {
  return (
    <ErrorBoundary>
      <Suspense fallback={<ShellSkeleton />}>
      <Switch>
        {/* Legacy /v3/* paths — v3 used to live under /v3/, now serves at /.
            Keep client-side redirects so old bookmarks and any in-app links
            land on the new root paths in both dev and production. */}
        <Route path="/v3"><Redirect to="/" /></Route>
        <Route path="/v3/">{() => <Redirect to="/" />}</Route>
        <Route path="/v3/:rest*">
          {(params) => <Redirect to={`/${params.rest ?? ""}`} />}
        </Route>
        {/* Public routes */}
        <Route path="/" component={() => <PublicOnlyRoute component={LandingPage} />} />
        <Route path="/features" component={FeaturesPage} />
        <Route path="/about" component={AboutPage} />
        <Route path="/privacy" component={PrivacyPage} />
        <Route path="/terms" component={TermsPage} />
        <Route path="/survey" component={() => <PublicOnlyRoute component={SurveyPage} />} />
        <Route path="/login" component={() => <PublicOnlyRoute component={LoginPage} />} />
        <Route path="/login/:rest*" component={() => <PublicOnlyRoute component={LoginPage} />} />
        <Route path="/sign-in" component={() => <PublicOnlyRoute component={LoginPage} />} />
        <Route path="/sign-in/:rest*" component={() => <PublicOnlyRoute component={LoginPage} />} />
        <Route path="/sign-up" component={() => <PublicOnlyRoute component={SignUpPage} />} />
        <Route path="/sign-up/:rest*" component={() => <PublicOnlyRoute component={SignUpPage} />} />
        <Route path="/accept-invite" component={AcceptInvite} />
        <Route path="/unlock" component={() => <ProtectedRoute component={UnlockReport} />} />

        {/* Protected app routes */}
        <Route path="/dashboard" component={() => <ProtectedRoute component={CEODashboard} />} />
        <Route path="/topics" component={() => <ProtectedRoute component={TopicsList} />} />
        <Route path="/topics/:id" component={() => <ProtectedRoute component={TopicDetail} />} />
        <Route path="/reviews" component={() => <ProtectedRoute component={ReviewsPage} />} />
        <Route path="/dept/:id" component={() => <ProtectedRoute component={DepartmentDashboard} />} />
        <Route path="/upload" component={() => <ProtectedRoute component={UploadData} />} />
        <Route path="/history" component={() => <ProtectedRoute component={History} />} />
        <Route path="/archive" component={() => <ProtectedRoute component={Archive} />} />
        <Route path="/scan-batches" component={() => <ProtectedRoute component={ScanBatchesPage} />} />
        <Route path="/scan-batches/:id" component={() => <ProtectedRoute component={ScanBatchesPage} />} />
        <Route path="/notifications" component={() => <ProtectedRoute component={Notifications} />} />
        <Route path="/settings" component={() => <ProtectedRoute component={Settings} />} />
        <Route path="/settings/:tab" component={() => <ProtectedRoute component={Settings} />} />
        <Route path="/integrations" component={() => <ProtectedRoute component={Integrations} />} />
        <Route path="/pricing" component={() => <ProtectedRoute component={Pricing} />} />
        <Route path="/payment/success" component={() => <ProtectedRoute component={PaymentSuccess} />} />
        <Route path="/onboarding" component={() => <ProtectedRoute component={Onboarding} />} />
        <Route path="/select-role" component={() => <ProtectedRoute component={SelectRolePage} />} />
        <Route path="/pending-access" component={() => <ProtectedRoute component={PendingAccess} />} />
        <Route path="/notebook" component={() => <ProtectedRoute component={NotebookList} />} />
        <Route path="/notebook/:id" component={() => <ProtectedRoute component={NotebookList} />} />
        <Route path="/tasks" component={() => <ProtectedRoute component={TasksPage} />} />
        <Route path="/ads" component={() => <ProtectedRoute component={AdsPage} />} />
        <Route path="/admin" component={() => <ProtectedRoute component={AdminConsole} />} />
        <Route path="/automations" component={() => <ProtectedRoute component={AutomationDashboard} />} />
        <Route path="/admin/onboard-client" component={() => <ProtectedRoute component={OnboardClient} />} />
        <Route path="/email" component={() => <ProtectedRoute component={EmailPage} />} />
        <Route path="/crm/contacts" component={() => <ProtectedRoute component={CrmContacts} />} />
        <Route path="/crm/contacts/:id" component={() => <ProtectedRoute component={CrmContactDetail} />} />
        <Route component={NotFound} />
      </Switch>
      </Suspense>
    </ErrorBoundary>
  );
}

const CLERK_PUBLISHABLE_KEY = import.meta.env.VITE_CLERK_PUBLISHABLE_KEY as string;
const CLERK_PROXY_URL = import.meta.env.VITE_CLERK_PROXY_URL as string | undefined;

function ClerkProviderWithRouter({ children }: { children: React.ReactNode }) {
  const [, setLocation] = useLocation();
  const basePath = (import.meta.env.BASE_URL || "").replace(/\/+$/, "");

  const stripBase = (path: string) => {
    if (basePath && path.startsWith(basePath)) {
      const stripped = path.slice(basePath.length);
      return stripped.startsWith("/") ? stripped : `/${stripped}`;
    }
    return path.startsWith("/") ? path : `/${path}`;
  };

  return (
    <ClerkProvider
      publishableKey={CLERK_PUBLISHABLE_KEY}
      {...(CLERK_PROXY_URL ? { proxyUrl: CLERK_PROXY_URL } : {})}
      routerPush={(to) => setLocation(stripBase(to))}
      routerReplace={(to) => setLocation(stripBase(to), { replace: true })}
      signInUrl="/login"
      signUpUrl="/sign-up"
      signInFallbackRedirectUrl="/dashboard"
      signUpFallbackRedirectUrl="/dashboard"
      appearance={clerkBaseAppearance}
    >
      {children}
    </ClerkProvider>
  );
}

function App() {
  return (
    <WouterRouter base={(import.meta.env.BASE_URL || "").replace(/\/$/, "")}>
      <ClerkProviderWithRouter>
        <QueryClientProvider client={queryClient}>
          <AuthProvider>
            <ClerkAuthBridge />
            <SentryRouteTracker />
            <AppBootstrap />
            <TooltipProvider>
              <Router />
              <Toaster />
            </TooltipProvider>
          </AuthProvider>
        </QueryClientProvider>
      </ClerkProviderWithRouter>
    </WouterRouter>
  );
}

export default App;
